import { FC, ReactNode, Ref, useMemo } from "react";
import { getFormattedThousand, includesInLC } from "@datassential/platform-ui-lib";
import {
    IconSocialsFb,
    IconSocialsInstagram, IconSocialsInstagramDark,
    IconSocialsPinterest,
    IconSocialsReddit,
    IconSocialsTiktok, IconSocialsTiktokDark,
    IconSocialsTumblr,
    IconSocialsTwitter, IconSocialsTwitterDark,
    IconSocialsYoutube,
} from "@icons";
import { FiExternalLink, FiMessageSquare } from "react-icons/fi";
import { ISocialPost, SocialPosts } from "@models/SocialPosts";
import { escapeRegExp } from "@helpers";
import './SocialPostCard.scss';

export interface SocialPostCardProps {
    post: ISocialPost;
    showFollowers?: boolean;
    isDark?: boolean;
    word?: string;
    association?: string;
    textRef?: Ref<HTMLDivElement>;
    testIdPrefix?: string;
}

export function getSocialIcon(source: string, isDark?: boolean): ReactNode {
    if (includesInLC(source, 'facebook')) return <IconSocialsFb/>;
    if (includesInLC(source, 'twitter')) return isDark ? <IconSocialsTwitterDark/> : <IconSocialsTwitter/>;
    if (includesInLC(source, 'instagram')) return isDark ? <IconSocialsInstagramDark/> : <IconSocialsInstagram/>;
    if (includesInLC(source, 'pinterest')) return <IconSocialsPinterest/>;
    if (includesInLC(source, 'youtube')) return <IconSocialsYoutube/>;
    if (includesInLC(source, 'tiktok')) return isDark ? <IconSocialsTiktokDark/> : <IconSocialsTiktok/>;
    if (includesInLC(source, 'tumblr')) return <IconSocialsTumblr/>;
    if (includesInLC(source, 'reddit')) return <IconSocialsReddit/>;
    return <FiMessageSquare/>;
}

function highlightText(text: string, word?: string, association?: string): string {
    let result = text;

    if (word) {
        const regexWord = new RegExp(`\\b(#?${escapeRegExp(word)})\\b`, 'gi');
        result = result.replace(regexWord, '<b>$1</b>');
    }

    if (association) {
        // association can be free text from the custom association search — escape it
        const regexAssociation = new RegExp(`\\b(#?${escapeRegExp(association)})\\b`, 'gi');
        result = result.replace(regexAssociation, '<b>$1</b>');
    }

    return result;
}

const SocialPostCard: FC<SocialPostCardProps> = ({
    post,
    showFollowers,
    isDark,
    word,
    association,
    textRef,
    testIdPrefix,
}) => {
    const { source, date, text, engagement, followersCount, url } = post;

    const icon = useMemo(() => getSocialIcon(source, isDark), [source, isDark]);

    const textHtml = useMemo(
        () => (word || association) ? highlightText(text, word, association) : text,
        [text, word, association],
    );

    return (
        <>
            {!!url && (
                <div
                    className="SocialPostCard__click-icon"
                    aria-hidden="true"
                    data-testid={testIdPrefix ? `${testIdPrefix}ClickIcon` : undefined}
                >
                    <FiExternalLink/>
                </div>
            )}
            <div className="SocialPostCard__header">
                <div className="SocialPostCard__header-left">
                    <div
                        className="SocialPostCard__header-icon"
                        data-testid={testIdPrefix ? `${testIdPrefix}Icon` : undefined}
                    >
                        {icon}
                    </div>
                    <div
                        className="SocialPostCard__header-text"
                        data-testid={testIdPrefix ? `${testIdPrefix}Source` : undefined}
                    >
                        {source}
                    </div>
                </div>
                <div
                    className="SocialPostCard__header-text"
                    data-testid={testIdPrefix ? `${testIdPrefix}Date` : undefined}
                >
                    {SocialPosts.formatDate(date)}
                </div>
            </div>
            <div
                ref={textRef}
                className="SocialPostCard__text"
                data-testid={testIdPrefix ? `${testIdPrefix}Text` : undefined}
                dangerouslySetInnerHTML={{ __html: textHtml }}
            />
            <div
                className="SocialPostCard__footer"
                data-testid={testIdPrefix ? `${testIdPrefix}Footer` : undefined}
            >
                {showFollowers && followersCount !== undefined && (
                    <span>followers: {getFormattedThousand(followersCount)}</span>
                )}
                <span>engagement count: {getFormattedThousand(engagement)}</span>
            </div>
        </>
    );
};

export default SocialPostCard;
